import { binarySearch } from '@kmccullough/binary-search';

import { parseDrawOptions } from 'rise-ui/utils/canvas/graph-draw.js';
import {
  alignForLine, alignForText, getCanvasTextSize, getCanvasWrappedSize,
} from 'rise-ui/utils/canvas.js';
import { maxOne } from 'rise-ui/utils/array.js';
import { defaultsTo, isSet, isUnset } from 'rise-ui/utils/nullish.js';

function parseGraphLegendOptions(options) {
  options = parseDrawOptions(options);
  Object.assign(options, {});
  return options;
}

function getColorSize(fontOptions) {
  const height = Math.round(getCanvasTextSize('M', fontOptions).height);
  return {
    width: Math.round(height * 1.25),
    height,
    padding: Math.round(height / 2),
  };
}

function getKeyGap(fontSize) {
  // Space between wrapped text and key color
  const inner = fontSize / 3;
  // Space between different legend keys
  const outer = inner * 1.5;
  return {
    inner: { width: inner, height: inner },
    outer: { width: outer, height: outer },
  };
}

/**
 * @name DrawGraphLegendKeyArg
 * @property {string} key
 */
/**
 * @name DrawGraphLegendOptionsArg
 * @extends DrawOptions
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [strokeStyle] (default: '#000')
 * @property {'top'|'right'|'bottom'|'left'} [orient] (default: 'bottom')
 * @property {[]} [tickArguments]
 * @property {function} [tickFormat]
 * @property {number} [tickPadding] (default: 3)
 * @property {number} [labelPadding]
 * @property {number} [labelPaddingX] (default: 8)
 * @property {number} [labelPaddingY] (default: 4)
 * @property {number} [tickSize] Set tickSizeInner and tickSizeOuter at once
 * @property {number} [tickSizeInner] (default: 6)
 * @property {number} [tickSizeOuter] (default: 6)
 * @property {number} [tickAngle] Angle of tick labels
 * @property {'start'|'center'|'end'} [tickAlign]
 * @property {[]} [tickValues]
 */
/**
 * Draw graph axis
 * @param {CanvasRenderingContext2D} context
 * @param {DrawGraphLegendKeyArg[]} keys
 * @param {DrawGraphLegendOptionsArg} options
 */
export function drawGraphLegend(context, keys, options) {
  if (!(keys?.length > 1)) {
    return;
  }

  let { width, height, columns, count } = options;
  count ??= keys?.length;
  const noWidth = isUnset(width);
  if (noWidth || isUnset(height) || isUnset(columns)) {
    const size = getGraphLegendSize(context, keys, options);
    if (!size) {
      return;
    }
    columns = size.columns;
    count = size.count;
    if (noWidth) {
      width = size.width;
    } else {
      height = size.height;
    }
  }
  const max = count - 1;

  options = parseGraphLegendOptions(options);
  let {
    paddingTop, paddingLeft, paddingRight, paddingBottom,
    fontMultiplier, fontFamily, lineHeight,
    strokeStyle, fontColor, center = false, outline = false,
  } = options;

  const fontSize = options.fontSize * fontMultiplier;
  const {
    inner: { width: innerGapWidth, height: innerGapHeight },
    outer: { width: outerGapWidth, height: outerGapHeight },
  } = getKeyGap(fontSize);
  const fontOptions = {
    gap: innerGapWidth,
    font: fontFamily,
    fontSize,
    lineHeight,
    context: options.context ?? context,
    width,
  };

  context.font = `${fontSize}px ${fontFamily}`;
  context.textAlign = 'start';
  context.textBaseline = 'top';
  context.lineCap = 'round';
  strokeStyle = strokeStyle || '#000';
  const fontFillStyle = fontColor || strokeStyle || '#000';

  const colorSize = getColorSize(fontOptions);
  const colorWidthWithPadding = {
    width: colorSize.width + colorSize.padding,
    height: colorSize.height,
  };

  // TODO make this work for legendBottom

  let x = paddingLeft;
  let y = 0;
  const columnWidth = Math.floor((width - paddingLeft - paddingRight + outerGapWidth) / columns - outerGapWidth);

  for (let i = 0; i <= max; ++i) {
    const { color, label } = keys[i];
    const size = measureKey([ colorWidthWithPadding, label ], columnWidth);
    if (i % columns === columns - 1) {
      x = paddingLeft;
      y += size.height + (i < max ? outerGapHeight : 0);
    } else {
      x += columnWidth + outerGapWidth;
    }
  }

  const actualHeight = y;
  x = paddingLeft;
  y = paddingTop + (height - actualHeight) / 2;

  if (outline) {
    context.beginPath();
    context.strokeStyle = '#000000';
    context.rect(alignForLine(0), alignForLine(0), alignForLine(width - 1), alignForLine(height - 1));
    context.stroke();
    context.beginPath();
    context.strokeStyle = '#8888ff';
    context.rect(
      alignForLine(0),
      alignForLine(y - paddingTop),
      alignForLine(width - 1),
      alignForLine(actualHeight - 1 + paddingTop + paddingTop)
    );
    context.stroke();
  }

  for (let i = 0; i <= max; ++i) {
    const { color, label } = keys[i];
    const size = drawKey(color, [ colorWidthWithPadding, label ], x, y, columnWidth);
    if (i % columns === columns - 1) {
      x = paddingLeft;
      y += size.height + outerGapHeight;
    } else {
      x += columnWidth + outerGapWidth;
    }
  }

  return;

  function measureKey(label, width) {
    fontOptions.width = width || Infinity;
    fontOptions.limit = Infinity;
    return getCanvasWrappedSize(label, fontOptions);
  }

  function drawKey(color, label, x, y, width) {
    fontOptions.width = width || Infinity;
    fontOptions.limit = Infinity;
    const size = getCanvasWrappedSize(label, fontOptions);
    const startX = x;

    if (outline) {
      context.beginPath();
      context.strokeStyle = '#88f';
      context.rect(alignForLine(x), alignForLine(y), Math.floor(size.width), Math.floor(size.height));
      context.stroke();
    }

    for (let l = 0, linesLen = size.lines.length; l < linesLen; ++l) {
      // For each line per legend key
      const line = size.lines[l];
      for (let i = 0, lineLen = line.line.length; i < lineLen; ++i) {
        // For each item per line per legend key
        const item = line.line[i];
        if (item.rect) {
          context.beginPath();
          context.strokeStyle = strokeStyle;
          context.fillStyle = color;
          context.rect(alignForLine(x), alignForLine(y), Math.floor(colorSize.width), Math.floor(colorSize.height));
          context.fill();
          context.stroke();
          x += item.rect.width;
          continue;
        }
        context.beginPath();
        if (outline) {
          context.strokeStyle = color;
          context.strokeRect(alignForLine(x), alignForLine(y), Math.floor(item.width), Math.floor(item.height));
        }
        context.fillStyle = fontFillStyle;
        context.fillText(item.text, alignForText(x), alignForText(y));
        x += item.width;
      }
      x = startX;
      y += line.height;
    }

    return size;
  }
}

export function getGraphLegendSize(context, keys, options) {
  if (!(keys?.length > 1)) {
    return;
  }

  let { width, minWidth, maxWidth, height, minHeight, maxHeight } = options;
  if (isUnset(width) && isUnset(minWidth) && isUnset(maxWidth)
    || isUnset(height) && isUnset(minHeight) && isUnset(maxHeight)
  ) {
    throw new Error('Expected width/minWidth/maxWidth and height/minHeight/maxHeight');
  }

  options = parseGraphLegendOptions(options);
  let {
    paddingTop, paddingBottom, paddingLeft, paddingRight,
    fontMultiplier, fontFamily, lineHeight,
  } = options;

  const isVertical = isSet(height);

  minWidth = defaultsTo(minWidth, defaultsTo(width, 0));
  maxWidth = defaultsTo(maxWidth, defaultsTo(width, minWidth));
  minHeight = defaultsTo(minHeight, defaultsTo(height, 0));
  maxHeight = defaultsTo(maxHeight, defaultsTo(height, minHeight));

  const paddingH = paddingLeft + paddingRight;
  const paddingV = paddingTop + paddingBottom;

  minWidth = Math.max(0, minWidth - paddingH);
  minHeight = Math.max(0, minHeight - paddingV);
  maxWidth = Math.max(minWidth, maxWidth - paddingH);
  maxHeight = Math.max(minHeight, maxWidth - paddingV);

  if (maxWidth <= 0 || maxHeight <= 0) {
    return;
  }

  const fontSize = options.fontSize * fontMultiplier;
  const {
    inner: { width: innerGapWidth, height: innerGapHeight },
    outer: { width: outerGapWidth, height: outerGapHeight },
  } = getKeyGap(fontSize);
  const fontOptions = {
    gap: innerGapWidth,
    font: fontFamily,
    fontSize,
    lineHeight,
    context: options.context ?? context,
    width: width ? width - paddingH : undefined,
  };

  const colorSize = getColorSize(fontOptions);
  const colorWidthWithPadding = {
    width: colorSize.width + colorSize.padding,
    height: colorSize.height,
  };

  // First preparation loop
  const meta = [];
  for (let k = 0; k < keys.length; ++k) {
    const { color, label } = keys[k];
    // Cache some argument structures for iterative calls
    const text = { color, label: [ colorWidthWithPadding, label ] };
    const keyMeta = meta[meta.length] = { text };
    // Measure minimum width of key color and first word on same line
    keyMeta.minWidth = measureKey(text, Infinity, 2).width;
    // Determine highest minimum width of those measured above
    meta.maxMinWidth = maxOne(keyMeta.minWidth, meta.maxMinWidth);
  }

  let { maxMinWidth } = meta;
  // Second preparation loop
  for (let i = 0; i < meta.length; ++i) {
    const keyMeta = meta[i];
    // Measure actual minimum width of key color and all words in label
    // Note: either keeps or expands maxMinWidth to accommodate big words on a line alone
    keyMeta.minWidth = measureKey(keyMeta.text, maxMinWidth).width;
    // Determine actual highest minimum width of those measured above
    meta.maxMinWidth = maxOne(keyMeta.minWidth, meta.maxMinWidth);
  }

  ({ maxMinWidth } = meta);

  // At this point we know: the minimum width necessary to render all words in a
  // legend key where the first word and legend color are on the same line, and
  // how many columns we can fit such that each column has equal width, but in
  // practice this minimum width may push keys off of the available height; so,
  // we still need to binary search through legend maxWidth, and iterate through
  // 1 to maxColumns, to find the smallest legend width that would show most the
  // legend keys, thereby also determining the optimal column count.

  let { columns, minColumns, maxColumns, minKeySizeRatio, minCharLineSize } = options;
  minColumns = Math.max(1, defaultsTo(minColumns, defaultsTo(columns, 1)));
  maxColumns = Math.max(minColumns, defaultsTo(maxColumns, defaultsTo(columns, Infinity)));
  const minLineSize = minCharLineSize ? getCanvasTextSize('M'.repeat(minCharLineSize), options).width : 0;
  maxMinWidth = Math.max(maxMinWidth, minLineSize);

  // TODO make this work for legendBottom

  let best;
  const search = binarySearch(minWidth, maxWidth).startHigh();
  for (const [ width ] of search) {

    const maxColumnsAtWidth = Math.min(maxColumns, maxMinWidth > width ? 1
      : Math.floor((width - maxMinWidth) / (outerGapWidth + maxMinWidth)) + 1
    );

    let currentBest;

    columnsLoop: for (let columns = minColumns; columns <= maxColumnsAtWidth; ++columns) {
      let y = 0;

      const columnWidth = Math.floor((width + outerGapWidth) / columns - outerGapWidth);
      if (columnWidth < maxMinWidth) {
        break;
      }

      let i;
      let loneColumnActualWidth = 0;
      for (i = 0; i < meta.length; ++i) {
        const keyMeta = meta[i];
        const size = measureKey(keyMeta.text, columnWidth);
        if (minKeySizeRatio && size.height && size.width / size.height < minKeySizeRatio) {
          break columnsLoop;
        }
        const proposedHeight = y + outerGapHeight + size.height;
        if (proposedHeight > maxHeight) {
          break;
        }
        if (columns === 1) {
          loneColumnActualWidth = maxOne(size.width, loneColumnActualWidth);
        }
        if (i % columns === columns - 1) {
          y = proposedHeight;
        }
      }

      if (!currentBest || i > currentBest.count || i === currentBest.count && (
        width < currentBest.width
          || width === currentBest.width && columns < currentBest.columns
      )) {
        const bestWidth = columns === 1 ? Math.ceil(loneColumnActualWidth) : width;
        currentBest = { count: i, width: bestWidth, columns };
      }
    }

    if (!best && currentBest) {
      best = currentBest;
      if (best.width < maxWidth) {
        search.nextValue(best.width);
      } else {
        search.lower();
      }
    } else if (!currentBest || currentBest.count < best.count) {
      search.higher();
    } else {
      if (currentBest.width < best.width) {
        best = currentBest;
      }
      search.lower();
    }
  }

  return best && {
    ...best,
    width: Math.ceil(defaultsTo(best.width, maxWidth) + paddingH),
    height: Math.ceil(defaultsTo(best.height, maxHeight) + paddingV),
  };

  function measureKey({ color, label }, width = Infinity, limit = Infinity) {
    fontOptions.width = width || Infinity;
    fontOptions.limit = limit || Infinity;
    return getCanvasWrappedSize(label, fontOptions);
  }
}
